import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Calendar,
  Card,
  Input,
  Label,
  LifetimeDiscountBadge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@nfp-tracker/components";
import {
  APP_WEB_ORIGIN,
  buildObservationsCsv,
  generatePDF,
  NFP_METHOD_OPTIONS,
  PRIVACY_POLICY_URL,
  SUPPORT_MAILTO_URL,
  TERMS_OF_USE_URL,
  usesExpandedMucus,
} from "@nfp-tracker/utils";
import { format } from "date-fns";
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  Download,
  FileText,
  Link2,
  Mail,
  Send,
  Trash2,
  Upload,
  UserCog,
  UserPlus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@nfp-tracker/components";
import { CsvImportDialog } from "../components/CsvImportDialog";
import { InstructorProfileSection } from "../components/InstructorProfileSection";
import { InstructorSharingSection } from "../components/InstructorSharingSection";
import { ROUTES } from "../constants/routes";
import { SETTINGS_ANCHORS } from "../constants/whatsNew";
import { useBillingPlans } from "../hooks/useBillingPlans";
import { OWNER_CONTEXT_QUERY_KEY, useOwnerContext } from "../hooks/useOwnerContext";
import { useSubscription } from "../hooks/useSubscription";
import { useSubscriptionStorefrontAccess } from "../hooks/useSubscriptionManagementAvailability";
import { supabase } from "../integrations/supabase/client";
import { Capacitor } from "@capacitor/core";
import { loadAccountProfile, setSpouseLink } from "../integrations/supabase/domain";
import { trackEvent } from "../integrations/analytics/client";
import { invokeEdgeFunction } from "../integrations/supabase/functions";
import { deriveCyclesFromObservations } from "../utils/derivedCycles";
import { fetchAllObservations } from "../utils/observationsCsvIo";
import {
  normalizeNotificationTime,
  syncDailyReminderNotifications,
} from "../utils/pushNotifications";
import { handleExternalError } from "../utils/errorSnackbar";
import {
  acceptSpouseInvite,
  buildInviteExpiry,
  extendInviteExpiry,
  getInviteAcceptErrorMessage,
  isInviteExpired,
} from "../utils/spouseInvites";
import { isNativeIosPurchasesAvailable } from "../plugins/appStorePurchases";
import { deriveSubscriptionDateMessaging } from "../utils/subscriptionStatus";

type ThemePreference = "light" | "dark" | "system";
type PendingInviteRecord = {
  id: string;
  inviter_user_id: string;
  invitee_email: string;
  token: string;
  expires_at: string;
  created_at: string;
  access_level: "read" | "write";
  counterparty_name: string | null;
  counterparty_email: string | null;
};

const Settings = () => {
  const appVersion = __APP_VERSION__;
  const { theme, setTheme } = useTheme();
  const termsUrl = (import.meta.env.VITE_TERMS_URL as string | undefined) ?? TERMS_OF_USE_URL;
  const privacyUrl = (import.meta.env.VITE_PRIVACY_URL as string | undefined) ?? PRIVACY_POLICY_URL;
  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [invites, setInvites] = useState<PendingInviteRecord[]>([]);
  const [spouseProfile, setSpouseProfile] = useState<any>(null);
  const [spouseLink, setSpouseLinkRecord] = useState<any>(null);
  const [spouseAccessLevel, setSpouseAccessLevel] = useState<"read" | "write">("write");
  const [updatingSpouseAccess, setUpdatingSpouseAccess] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationTime, setNotificationTime] = useState("07:00");
  const [pushNotificationsEnabled, setPushNotificationsEnabled] =
    useState(false);
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editAge, setEditAge] = useState("");
  const [editWeight, setEditWeight] = useState("");
  const [isEditingCouple, setIsEditingCouple] = useState(false);
  const [editGoal, setEditGoal] = useState("");
  const [editMethods, setEditMethods] = useState<string[]>([]);
  const [editMethodOther, setEditMethodOther] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRemoveSpouseDialog, setShowRemoveSpouseDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [duplicateInvite, setDuplicateInvite] = useState<PendingInviteRecord | null>(null);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  const [waitlistCode, setWaitlistCode] = useState("");
  const [claimingCode, setClaimingCode] = useState(false);
  const [themePreference, setThemePreference] = useState<ThemePreference>("system");
  const [optionalFields, setOptionalFields] = useState<any>({
    temperature: true,
    mucus: true,
    monitor: true,
    bleeding: true,
    intercourse: true,
    symptoms: true,
    notes: true,
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSubscriptionManagementAvailable } =
    useSubscriptionStorefrontAccess();

  // Shared, session-cached auth/owner/permission context (same hook Dashboard.tsx and
  // Subscription.tsx use) — dedupes the owner-resolution chain against whatever other page
  // already warmed this cache instead of re-running getUser -> resolveOwnerContext ->
  // loadOwnerAccount from scratch on every mount.
  const ownerContextQuery = useOwnerContext();
  const ownerContext = ownerContextQuery.data;
  const couple = ownerContext?.status === "ready" ? ownerContext.couple : null;
  const actorUserId = ownerContext?.status === "ready" ? ownerContext.actorUserId : undefined;
  // CSV import writes the owner's observations: tracking owner or a spouse with a write link
  // only — never instructors (their client view is read-only) or read-only viewers.
  const canImportObservations =
    ownerContext?.status === "ready" &&
    ownerContext.canWriteObservations &&
    ownerContext.actorRole !== "instructor" &&
    !ownerContext.viewingClientUserId;
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const loading = ownerContextQuery.isLoading || profileLoading;

  // Only the subscription card below reads plan pricing, and `get-billing-plans` needs a user
  // JWT — so this waits for the session to resolve and skips the call entirely on builds where
  // that card never renders.
  const billingPlans = useBillingPlans({
    enabled: ownerContext?.status === "ready" && isSubscriptionManagementAvailable,
  }).data;
  const hasFounderPricing = billingPlans?.hasFounderPricing ?? false;
  const billingPlansByPriceId = billingPlans?.byPriceId ?? {};
  const billingPlansByPlanKey = billingPlans?.byPlanKey ?? {};

  const {
    status: subscriptionStatus,
    trialEndDate,
    subscriptionEndDate,
    subscriptionRenewalDate,
    subscriptionAutoRenews,
    freeForLife,
    planKey,
    stripePriceId,
    billingProvider,
    appStoreProductId,
  } = useSubscription(couple?.id);

  const refreshActorProfile = async () => {
    if (!actorUserId) return;
    const profileData = await loadAccountProfile(actorUserId);
    setProfile(profileData);
    setNotificationsEnabled(profileData?.notification_enabled ?? true);
    setNotificationTime(normalizeNotificationTime(profileData?.notification_time));
    setPushNotificationsEnabled(profileData?.push_notifications_enabled ?? false);
    if (profileData?.theme_preference === "light" || profileData?.theme_preference === "dark" || profileData?.theme_preference === "system") {
      setThemePreference(profileData.theme_preference);
    }
    setEditFirstName(profileData?.first_name || "");
    setEditLastName(profileData?.last_name || "");
    setEditAge(profileData?.age?.toString() || "");
    setEditWeight(
      profileData?.weight_kg
        ? (profileData.weight_kg * 2.20462).toFixed(0)
        : ""
    );
  };

  const refreshSpouseAndInvites = async () => {
    const { data: spouseSummaryRows } = await supabase
      .rpc("get_my_spouse_link_summary");

    const spouseSummary = spouseSummaryRows?.[0] as {
      link_id: string;
      owner_user_id: string;
      grantee_user_id: string;
      access_level: "read" | "write";
      counterparty_user_id: string | null;
      counterparty_name: string | null;
      counterparty_email: string | null;
      counterparty_role: "tracking" | "viewing" | null;
    } | undefined;

    const spouseLinkRecord = spouseSummary
      ? {
          id: spouseSummary.link_id,
          owner_user_id: spouseSummary.owner_user_id,
          grantee_user_id: spouseSummary.grantee_user_id,
          access_level: spouseSummary.access_level,
          is_active: true,
        }
      : null;

    setSpouseLinkRecord(spouseLinkRecord);
    setSpouseAccessLevel((spouseSummary?.access_level || "write") as "read" | "write");

    // Load pending spouse invites with counterparty identity.
    const { data: invitesData } = await supabase
      .rpc("list_my_pending_spouse_invites_with_identity");

    setInvites((invitesData || []) as PendingInviteRecord[]);
    setDuplicateInvite(null);

    if (spouseSummary) {
      const [firstName, ...rest] = (spouseSummary.counterparty_name || "").split(" ");
      setSpouseProfile({
        user_id: spouseSummary.counterparty_user_id,
        first_name: firstName || null,
        last_name: rest.length ? rest.join(" ") : null,
        email: spouseSummary.counterparty_email,
        role: spouseSummary.counterparty_role,
      });
    } else {
      setSpouseProfile(null);
    }
  };

  const refreshOwnerContext = () =>
    queryClient.invalidateQueries({ queryKey: OWNER_CONTEXT_QUERY_KEY });

  useEffect(() => {
    if (theme === "light" || theme === "dark" || theme === "system") {
      setThemePreference(theme);
    }
  }, [theme]);

  useEffect(() => {
    if (ownerContext?.status === "unauthenticated") {
      navigate(ROUTES.auth);
    } else if (ownerContext?.status === "needs-onboarding") {
      navigate(ROUTES.onboarding);
    }
  }, [ownerContext, navigate]);

  // Actor's own account/tracking-profile fields (age, weight, notifications, theme) — runs in
  // parallel with the owner-context query above instead of chaining after it.
  useEffect(() => {
    if (!actorUserId) return;
    let cancelled = false;

    (async () => {
      try {
        await refreshActorProfile();
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [actorUserId]);

  // Spouse link summary + pending invites — depends on the owner context resolving, but no
  // longer blocks the actor-profile or billing-plan fetches above.
  useEffect(() => {
    if (!couple) return;

    setEditGoal(couple.goal || "");
    setEditMethods(couple.methods ?? []);
    setEditMethodOther(couple.method_other || "");
    setOptionalFields(
      couple.optional_fields || {
        temperature: true,
        mucus: true,
        monitor: true,
        bleeding: true,
        intercourse: true,
        symptoms: true,
        notes: true,
      }
    );

    let cancelled = false;
    (async () => {
      try {
        await refreshSpouseAndInvites();
      } catch (error) {
        if (!cancelled) console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Keyed on the owner id (not the `couple` object) since useOwnerContext hands back a new
    // object reference on every background revalidation — keying on the object would reset
    // the couple-settings edit form's in-progress values whenever that cache silently refreshes.
  }, [couple?.id]);

  // Deep links from Getting Started (/settings#notifications, #tracked-fields,
  // #field-ovulation-test, #import-data, #instructor-sharing) scroll straight to that section. Waits for
  // `loading` to clear so the section is actually mounted before we scroll to it.
  const { hash: locationHash } = useLocation();
  useEffect(() => {
    if (loading) return;
    const anchor = decodeURIComponent(locationHash.replace(/^#/, ""));
    if (!(Object.values(SETTINGS_ANCHORS) as string[]).includes(anchor)) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById(anchor)
        // Sections land heading-first (so it isn't pushed above the fold on phones);
        // single controls like the notifications card / OPK switch are centered.
        ?.scrollIntoView({
          behavior: "smooth",
          block:
            anchor === SETTINGS_ANCHORS.trackedFields ||
            anchor === SETTINGS_ANCHORS.importData ||
            anchor === SETTINGS_ANCHORS.instructorSharing
              ? "start"
              : "center",
        });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [loading, locationHash]);

  const instructorName: string | null = null;
  const instructorEmail: string | null = null;
  const actorRole = (profile?.role || "viewing") as "tracking" | "viewing" | "instructor";
  const isActorTracking = actorRole === "tracking";
  const isActorViewing = actorRole === "viewing";
  const isActorInstructor = actorRole === "instructor";
  const isActorOwner = Boolean(profile?.id && couple?.id && profile.id === couple.id);
  const hasActiveSpouseLink = Boolean(spouseLink?.id);
  const normalizedProfileEmail = (profile?.email || "").trim().toLowerCase();
  const hasStripeSubscription = Boolean(couple?.stripe_customer_id) || billingProvider === "stripe";
  const hasAppleSubscription = Boolean(appStoreProductId) || billingProvider === "apple";
  const billingProviderLabel = billingProvider === "apple"
    ? "Apple App Store"
    : hasStripeSubscription
      ? "Web subscription"
      : "Not set";
  const planKeyFromCouple = planKey === "monthly" || planKey === "annual"
    ? planKey
    : null;
  const settingsCurrentPlan = stripePriceId
    ? billingPlansByPriceId[stripePriceId] ?? null
    : null;
  const settingsCurrentPlanByKey = planKeyFromCouple
    ? billingPlansByPlanKey[planKeyFromCouple] ?? null
    : null;
  const settingsCurrentPlanLabel = freeForLife
    ? "Free for Life"
    : settingsCurrentPlan?.displayName
      ?? settingsCurrentPlanByKey?.displayName
      ?? (planKeyFromCouple === "monthly" ? "Monthly" : planKeyFromCouple === "annual" ? "Annual" : "Unknown");
  const settingsCurrentPriceLabel = freeForLife
    ? "No charge"
    : settingsCurrentPlan
      ? `${settingsCurrentPlan.priceDisplay}/${settingsCurrentPlan.interval}`
      : settingsCurrentPlanByKey
        ? `${settingsCurrentPlanByKey.priceDisplay}/${settingsCurrentPlanByKey.interval}`
      : null;

  const isIncomingInvite = (invite: PendingInviteRecord) =>
    Boolean(normalizedProfileEmail) &&
    invite.inviter_user_id !== profile?.id &&
    invite.invitee_email.trim().toLowerCase() === normalizedProfileEmail;

  const getInviteUrl = (token: string) => `${APP_WEB_ORIGIN}/invite/${token}`;

  const handleResendInvite = async (invite: any) => {
    try {
      console.log("Resending invite to:", invite.invitee_email);
      // Resending revives a stale invite instead of leaving the invitee with an
      // invite they can never accept.
      await extendInviteExpiry(invite.id);
      const { data, error } = await invokeEdgeFunction(
        "send-invite-email",
        {
          body: {
            inviteId: invite.id,
            recipientEmail: invite.invitee_email,
            recipientName: "there",
            senderName:
              `${profile?.first_name || ""} ${
                profile?.last_name || ""
              }`.trim() || "Your spouse",
            inviteToken: invite.token,
          },
        }
      );

      if (error) {
        console.error("Resend invite error:", error);
        throw error;
      }

      console.log("Invite resent successfully:", data);

      toast({
        title: "Invitation resent",
        description: `Sent to ${invite.invitee_email}`,
      });
      await refreshSpouseAndInvites();
    } catch (error: any) {
      handleExternalError(toast, error, {
        logPrefix: "Error in handleResendInvite:",
      });
    }
  };

  const handleCopyInviteLink = async (invite: PendingInviteRecord) => {
    try {
      await navigator.clipboard.writeText(getInviteUrl(invite.token));
      toast({
        title: "Invite link copied",
        description: "Share the existing invite link with your spouse.",
      });
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        title: "Unable to copy link",
        logPrefix: "Error copying invite link:",
      });
    }
  };

  const handleAcceptInvite = async (inviteId: string) => {
    setProcessingInviteId(inviteId);
    try {
      await acceptSpouseInvite(inviteId);

      toast({
        title: "Invite accepted",
        description: "Your spouse link is now active.",
      });
      refreshOwnerContext();
      await refreshSpouseAndInvites();
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        title: "Could not accept invite",
        message: getInviteAcceptErrorMessage(error),
        logPrefix: "Error accepting invite:",
      });
      // An expired/canceled invite stays listed; refresh so the card reflects it.
      await refreshSpouseAndInvites();
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleDeclineInvite = async (inviteId: string) => {
    setProcessingInviteId(inviteId);
    try {
      const { data: declined, error } = await supabase
        .rpc("decline_link_invite", { invite_id: inviteId });

      if (error || !declined) {
        throw error || new Error("Unable to decline invite.");
      }

      toast({
        title: "Invite declined",
        description: "The pending spouse invite has been removed.",
      });
      await refreshSpouseAndInvites();
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        title: "Could not decline invite",
        logPrefix: "Error declining invite:",
      });
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleThemePreferenceChange = async (nextTheme: ThemePreference) => {
    if (nextTheme === themePreference) {
      return;
    }

    const previousTheme = themePreference;
    setThemePreference(nextTheme);
    setTheme(nextTheme);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return;
      }

      const { error } = await supabase
        .from("user_accounts")
        .update({ theme_preference: nextTheme })
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Theme updated",
        description: `Using ${nextTheme} theme.`,
      });
    } catch (error: unknown) {
      setThemePreference(previousTheme);
      setTheme(previousTheme);
      handleExternalError(toast, error, {
        title: "Unable to save theme",
        logPrefix: "Error saving theme preference:",
      });
    }
  };

  const availableInvites = invites.filter((invite) => !isIncomingInvite(invite));
  const incomingInvites = invites.filter((invite) => isIncomingInvite(invite));

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await supabase.from("link_invites").delete().eq("id", inviteId);
      setInvites(invites.filter((i) => i.id !== inviteId));

      toast({
        title: "Invitation cancelled",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !couple || !profile) return;

    if (!isActorTracking || !isActorOwner) {
      toast({
        title: "Action unavailable",
        description: "Only the tracking account owner can send spouse invites.",
        variant: "destructive",
      });
      return;
    }

    const normalizedInviteEmail = inviteEmail.trim().toLowerCase();

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedInviteEmail)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    // Check if already has a spouse
    if (couple.partner_user_id) {
      toast({
        title: "Spouse already connected",
        description: "You can only have one spouse at a time",
        variant: "destructive",
      });
      return;
    }

    setSendingInvite(true);
    try {
      const { data: existingInvitesRaw, error: existingInviteError } = await supabase
        .from("link_invites")
        .select("id, inviter_user_id, invitee_email, token, expires_at, created_at, access_level")
        .ilike("invitee_email", normalizedInviteEmail)
        .eq("link_type", "spouse")
        .is("accepted_at", null)
        .is("canceled_at", null)
        .limit(1);

      const existingInvites = (existingInvitesRaw || []) as PendingInviteRecord[];

      if (existingInviteError) {
        throw existingInviteError;
      }

      if (existingInvites && existingInvites.length > 0) {
        setDuplicateInvite(existingInvites[0] as PendingInviteRecord);
        toast({
          title: "Invite already exists",
          description: "That email already has a pending invite. Resend or copy the existing invite below.",
          variant: "destructive",
        });
        return;
      }
      setDuplicateInvite(null);

      // Create invite token
      const token = crypto.randomUUID().replace(/-/g, "");
      const expiresAt = buildInviteExpiry();

      console.log("Creating invite for:", normalizedInviteEmail);

      // Insert invite
      const { data: newInvite, error: inviteError } = await supabase
        .from("link_invites")
        .insert({
          inviter_user_id: couple.id,
          invitee_email: normalizedInviteEmail,
          link_type: "spouse",
          access_level: "write",
          token,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (inviteError) {
        console.error("Error creating invite:", inviteError);
        throw inviteError;
      }

      console.log("Invite created, sending email...");

      // Send email
      const { data: emailData, error: emailError } =
        await invokeEdgeFunction("send-invite-email", {
          body: {
            inviteId: newInvite.id,
            recipientEmail: normalizedInviteEmail,
            recipientName: normalizedInviteEmail.split("@")[0],
            senderName:
              `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
              "Your spouse",
            inviteToken: token,
          },
        });

      if (emailError) {
        console.error("Email sending error:", emailError);
        throw emailError;
      }

      console.log("Email sent successfully:", emailData);

      toast({
        title: "Invitation sent",
        description: `Sent to ${normalizedInviteEmail}`,
      });

      setInviteEmail("");
      void refreshSpouseAndInvites();
    } catch (error: any) {
      handleExternalError(toast, error, {
        logPrefix: "Error in handleSendInvite:",
      });
    } finally {
      setSendingInvite(false);
    }
  };

  const handleUpdateSpouseAccess = async (nextAccessLevel: "read" | "write") => {
    if (!spouseLink || !isActorTracking || !isActorOwner) {
      return;
    }

    setUpdatingSpouseAccess(true);
    try {
      const { error } = await supabase
        .from("user_data_links")
        .update({ access_level: nextAccessLevel })
        .eq("id", spouseLink.id);

      if (error) {
        throw error;
      }

      setSpouseAccessLevel(nextAccessLevel);
      setSpouseLinkRecord({ ...spouseLink, access_level: nextAccessLevel });
      toast({
        title: "Access level updated",
        description: `Spouse access is now ${nextAccessLevel}.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingSpouseAccess(false);
    }
  };

  const handleNotificationUpdate = async (
    overrides: {
      notificationsEnabled?: boolean;
      notificationTime?: string;
      pushNotificationsEnabled?: boolean;
    } = {},
    options: {
      allowPermissionPrompt?: boolean;
      requestPushRegistration?: boolean;
      showSuccessToast?: boolean;
    } = {}
  ) => {
    const nextNotificationsEnabled =
      overrides.notificationsEnabled ?? notificationsEnabled;
    const nextNotificationTime = normalizeNotificationTime(
      overrides.notificationTime ?? notificationTime
    );
    const nextPushNotificationsEnabled =
      overrides.pushNotificationsEnabled ?? pushNotificationsEnabled;
    const {
      allowPermissionPrompt = false,
      requestPushRegistration = false,
      showSuccessToast = true,
    } = options;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return { success: false as const };
      }

      const { error } = await supabase
        .from("user_accounts")
        .update({
          notification_enabled: nextNotificationsEnabled,
          notification_time: nextNotificationTime,
          push_notifications_enabled: nextPushNotificationsEnabled,
        })
        .eq("user_id", user.id);

      if (error) throw error;

      const syncResult = await syncDailyReminderNotifications(
        {
          notificationsEnabled: nextNotificationsEnabled,
          pushNotificationsEnabled: nextPushNotificationsEnabled,
          notificationTime: nextNotificationTime,
        },
        {
          allowPermissionPrompt,
          requestPushRegistration,
        }
      );

      if (
        nextNotificationsEnabled &&
        nextPushNotificationsEnabled &&
        syncResult.permissionGranted === false
      ) {
        const { error: rollbackError } = await supabase
          .from("user_accounts")
          .update({ push_notifications_enabled: false })
          .eq("user_id", user.id);

        if (rollbackError) throw rollbackError;

        setPushNotificationsEnabled(false);
        toast({
          title: "Permission Required",
          description:
            "Enable notification permission to receive push reminders.",
          variant: "destructive",
        });
        return { success: true as const, permissionGranted: false };
      }

      if (showSuccessToast) {
        toast({
          title: "Settings saved",
          description: "Notification preferences updated.",
        });
      }

      return {
        success: true as const,
        permissionGranted: syncResult.permissionGranted,
      };
    } catch (error: any) {
      handleExternalError(toast, error, {
        logPrefix: "Error saving notification preferences:",
      });
      return { success: false as const };
    }
  };

  const handleExportCSV = async () => {
    try {
      if (!couple) return;

      // Cycle days come from the full history even when exporting a date range: deriving them
      // from only the in-range rows would restart CD 1 at the first exported day.
      const allObservations = await fetchAllObservations(couple.id);
      const { observations: derived } = deriveCyclesFromObservations(allObservations, couple.methods);
      const from = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : null;
      const to = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : null;
      const inRange = derived.filter(
        (obs) => (!from || obs.observation_date >= from) && (!to || obs.observation_date <= to)
      );

      const csv = buildObservationsCsv(inRange);

      // Download
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nfp-tracker-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);

      toast({
        title: "Export complete",
        description: `${inRange.length} day${inRange.length === 1 ? "" : "s"} exported to CSV.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!couple) return;

      // Same as CSV: derive from the full (paged) history so a date range doesn't restart CD 1 at
      // its first day, then keep the observations in range and the cycles that overlap it.
      const allObservations = await fetchAllObservations(couple.id);
      const { cycles: allCycles, observations: allDerived } = deriveCyclesFromObservations(
        allObservations,
        couple?.methods
      );
      const from = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : null;
      const to = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : null;
      const normalizedObs = allDerived.filter(
        (obs) => (!from || obs.observation_date >= from) && (!to || obs.observation_date <= to)
      );
      const cycles = allCycles.filter(
        (cycle) =>
          (!to || cycle.start_date <= to) && (!from || !cycle.end_date || cycle.end_date >= from)
      );

      generatePDF(
        normalizedObs || [],
        cycles || [],
        `${profile?.first_name} ${profile?.last_name}`,
        couple?.methods,
        couple?.timezone
      );

      toast({
        title: "Export Complete",
        description: "Your PDF report has been generated.",
      });
    } catch (error: any) {
      toast({
        title: "Export Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const firstName = editFirstName.trim();
      const lastName = editLastName.trim();
      const nameChanged =
        firstName !== (profile?.first_name || "") || lastName !== (profile?.last_name || "");

      // Only a name edit needs a first name; accounts that never had one can still save
      // age/weight without being forced to add it.
      if (nameChanged && !firstName) {
        toast({
          title: "First name required",
          description: "Enter your first name to save your profile.",
          variant: "destructive",
        });
        return;
      }

      if (nameChanged) {
        const { error: accountError } = await supabase
          .from("user_accounts")
          .update({ first_name: firstName, last_name: lastName || null })
          .eq("user_id", user.id);
        if (accountError) throw accountError;

        // Auth metadata feeds analytics identity and the sidebar greeting.
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { first_name: firstName, last_name: lastName },
        });
        if (metadataError) throw metadataError;
      }

      if (isActorTracking) {
        const { error } = await supabase
          .from("user_tracking_profiles")
          .upsert({
            user_id: user.id,
            age: editAge ? parseInt(editAge) : null,
            weight_kg: editWeight ? parseFloat(editWeight) * 0.453592 : null,
          });

        if (error) throw error;
      }

      toast({
        title: "Profile Updated",
        description: "Your profile information has been saved.",
      });

      setIsEditingProfile(false);
      await refreshActorProfile();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateCouple = async () => {
    try {
      if (!couple) return;
      if (!isActorTracking) return;
      if (editMethods.length === 0) {
        toast({
          title: "Missing info",
          description: "Select at least one method before saving.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("user_tracking_profiles")
        .update({
          goal: editGoal,
          methods: editMethods,
          // Dual-write the legacy singular column too — old app builds (web tabs that
          // haven't refreshed, un-updated iOS/Android installs) still read `method`
          // directly until the methods-array migration's Phase 2 drops it. Remove once
          // that phase actually runs.
          method: editMethods[0] ?? null,
          method_other: editMethods.includes("Other") ? editMethodOther : null,
          optional_fields: optionalFields,
        })
        .eq("user_id", couple.id);

      if (error) throw error;

      toast({
        title: "Settings Updated",
        description: "Your couple settings have been saved.",
      });

      setIsEditingCouple(false);
      refreshOwnerContext();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRemoveSpouse = async () => {
    try {
      if (!couple) return;

      if (!isActorTracking || !isActorOwner) {
        toast({
          title: "Action unavailable",
          description: "Only the tracking account owner can remove spouse links.",
          variant: "destructive",
        });
        return;
      }

      await setSpouseLink(couple.id, null);
      const error = null;

      if (error) throw error;

      toast({
        title: "Spouse Removed",
        description: "Your spouse has been removed from the shared account.",
      });

      setShowRemoveSpouseDialog(false);
      refreshOwnerContext();
      await refreshSpouseAndInvites();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleClaimWaitlistCode = async () => {
    if (!waitlistCode.trim() || !couple || !profile) return;

    setClaimingCode(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: codeValid, error: codeError } = await supabase.rpc(
        "claim_waitlist_code",
        {
          code_to_claim: waitlistCode.toUpperCase().trim(),
          user_id: user.id,
        }
      );

      if (codeError) {
        throw codeError;
      }

      if (codeValid) {
        // Extend trial by 30 days from now
        const newTrialEnd = new Date();
        newTrialEnd.setDate(newTrialEnd.getDate() + 30);

        await supabase
          .from("user_subscriptions")
          .update({ trial_end_date: newTrialEnd.toISOString() })
          .eq("owner_user_id", couple.id);

        toast({
          title: "Code Applied!",
          description: "You've received 30 days of free access to NFP Tracker.",
        });

        setWaitlistCode("");
      } else {
        toast({
          title: "Invalid Code",
          description: "This code is not valid or has already been used.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setClaimingCode(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Only owners can delete shared owner-scoped data.
      if (couple && isActorOwner) {
        await supabase.from("observations").delete().eq("owner_user_id", couple.id);
        await supabase.from("link_invites").delete().eq("inviter_user_id", couple.id);
        await supabase.from("user_data_links").delete().eq("owner_user_id", couple.id);
        await supabase.from("user_subscriptions").delete().eq("owner_user_id", couple.id);
      } else {
        await supabase.from("user_data_links").delete().eq("grantee_user_id", user.id);
      }

      await supabase.from("user_tracking_profiles").delete().eq("user_id", user.id);
      await supabase.from("user_accounts").delete().eq("user_id", user.id);

      // Sign out
      await supabase.auth.signOut();

      toast({
        title: "Account Deleted",
        description: "Your account and all data have been permanently deleted.",
      });

      navigate(ROUTES.auth);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-4 pb-8">
        <Card className="p-6 space-y-6 shadow-[var(--shadow-soft)] animate-fade-in">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  Your Profile
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (isEditingProfile) {
                      setEditFirstName(profile?.first_name || "");
                      setEditLastName(profile?.last_name || "");
                    }
                    setIsEditingProfile(!isEditingProfile);
                  }}
                >
                  {isEditingProfile ? "Cancel" : "Edit"}
                </Button>
              </div>

              {isEditingProfile ? (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="editFirstName">First name</Label>
                      <Input
                        id="editFirstName"
                        value={editFirstName}
                        onChange={(e) => setEditFirstName(e.target.value)}
                        placeholder="First name"
                        autoComplete="given-name"
                        className="mt-1"
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <Label htmlFor="editLastName">Last name</Label>
                      <Input
                        id="editLastName"
                        value={editLastName}
                        onChange={(e) => setEditLastName(e.target.value)}
                        placeholder="Last name"
                        autoComplete="family-name"
                        className="mt-1"
                        maxLength={100}
                      />
                    </div>
                  </div>
                  {isActorTracking && (
                    <>
                      <div>
                        <Label htmlFor="editAge">Age</Label>
                        <Input
                          id="editAge"
                          type="number"
                          value={editAge}
                          onChange={(e) => setEditAge(e.target.value)}
                          placeholder="Age"
                          className="mt-1"
                          min="18"
                          max="100"
                        />
                      </div>
                      <div>
                        <Label htmlFor="editWeight">Weight (lbs)</Label>
                        <Input
                          id="editWeight"
                          type="number"
                          value={editWeight}
                          onChange={(e) => setEditWeight(e.target.value)}
                          placeholder="Weight"
                          className="mt-1"
                          min="50"
                          max="500"
                        />
                      </div>
                    </>
                  )}
                  {isActorTracking && profile?.sex === "female" && (
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label htmlFor="postpartum">Postpartum</Label>
                        <p className="text-sm text-muted-foreground">
                          Enable if currently in postpartum phase
                        </p>
                      </div>
                      <Switch
                        id="postpartum"
                        checked={profile?.is_postpartum || false}
                        onCheckedChange={async (checked) => {
                          const {
                            data: { user },
                          } = await supabase.auth.getUser();
                          if (!user) return;

                          await supabase
                            .from("user_tracking_profiles")
                            .upsert({ user_id: user.id, is_postpartum: checked });

                          await refreshActorProfile();
                          toast({
                            title: "Updated",
                            description: `Postpartum status ${
                              checked ? "enabled" : "disabled"
                            }`,
                          });
                        }}
                      />
                    </div>
                  )}
                  {isActorViewing && (
                    <p className="text-sm text-muted-foreground">
                      Viewing accounts do not maintain tracking-profile fields.
                    </p>
                  )}
                  <Button onClick={handleUpdateProfile} className="w-full">
                    Save Changes
                  </Button>
                </div>
              ) : (
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    {profile?.first_name} {profile?.last_name}
                  </p>
                  <p>{profile?.email}</p>
                  <p>Role: {profile?.role === "tracking" ? "Tracking" : profile?.role === "instructor" ? "Instructor" : "Viewing"}</p>
                  <p>Timezone: {profile?.timezone || "UTC"}</p>
                  <p>Gender: {profile?.sex || "Not set"}</p>
                  {isActorTracking && profile?.age && <p>Age: {profile.age}</p>}
                  {isActorTracking && profile?.weight_kg && (
                    <p>
                      Weight: {(profile.weight_kg * 2.20462).toFixed(0)} lbs
                    </p>
                  )}
                  {isActorTracking && profile?.sex === "female" && (
                    <div className="flex items-center gap-2">
                      <p>Postpartum:</p>
                      <Badge
                        variant={profile?.is_postpartum ? "default" : "outline"}
                      >
                        {profile?.is_postpartum ? "Yes" : "No"}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </div>

            {isActorInstructor && actorUserId && (
              <InstructorProfileSection userId={actorUserId} />
            )}

            {couple && !isActorInstructor && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-base font-semibold">
                      Couple Settings
                    </Label>
                    {isActorTracking && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditingCouple(!isEditingCouple)}
                      >
                        {isEditingCouple ? "Cancel" : "Edit"}
                      </Button>
                    )}
                  </div>

                  {isActorTracking && isEditingCouple ? (
                    <div className="mt-3 space-y-3">
                      <div>
                        <Label htmlFor="editGoal">Goal</Label>
                        <Select value={editGoal} onValueChange={setEditGoal}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select your goal" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="TTC">
                              Trying to Conceive
                            </SelectItem>
                            <SelectItem value="AP">
                              Avoiding Pregnancy
                            </SelectItem>
                            <SelectItem value="Pregnant">
                              Currently Pregnant
                            </SelectItem>
                            <SelectItem value="Single">
                              Single & Tracking
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Method(s)</Label>
                        <p className="text-xs text-muted-foreground mt-1 mb-2">
                          Select every method you use — combining Marquette monitor
                          readings with Creighton mucus observations, for example.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {NFP_METHOD_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                setEditMethods((current) =>
                                  current.includes(option.value)
                                    ? current.filter((m) => m !== option.value)
                                    : [...current, option.value]
                                )
                              }
                              className={`p-2 rounded-lg border-2 transition-all text-sm font-medium ${
                                editMethods.includes(option.value)
                                  ? "border-primary bg-primary/10"
                                  : "border-border"
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {editMethods.includes("Other") && (
                        <div>
                          <Label htmlFor="editMethodOther">
                            Specify Method
                          </Label>
                          <Input
                            id="editMethodOther"
                            value={editMethodOther}
                            onChange={(e) => setEditMethodOther(e.target.value)}
                            placeholder="Enter method name"
                            className="mt-1"
                          />
                        </div>
                      )}
                      <Button
                        onClick={handleUpdateCouple}
                        disabled={editMethods.length === 0}
                        className="w-full"
                      >
                        Save Changes
                      </Button>
                    </div>
                  ) : isActorTracking ? (
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <p>
                        Goal:{" "}
                        {couple.goal === "TTC"
                          ? "Trying to Conceive"
                          : couple.goal === "AP"
                          ? "Avoiding Pregnancy"
                          : couple.goal === "Pregnant"
                          ? "Currently Pregnant"
                          : "Single & Tracking"}
                      </p>
                      <p>Method: {couple.methods?.join(", ")}</p>
                      {couple.method_other && <p>({couple.method_other})</p>}
                    </div>
                  ) : (
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <p>Managed by the linked tracking account.</p>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                    <UserPlus className="h-4 w-4" />
                    Spouse Management
                  </Label>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      {hasActiveSpouseLink ? (
                        <div className="text-sm text-muted-foreground">
                          <p>
                            {(spouseProfile?.first_name || "") + " " + (spouseProfile?.last_name || "")
                              ? `${spouseProfile?.first_name || ""} ${spouseProfile?.last_name || ""}`.trim()
                              : "Linked spouse"}
                          </p>
                          <p>{spouseProfile?.email || "Email unavailable"}</p>
                          <p>Role: {spouseProfile?.role === "tracking" ? "Tracking" : spouseProfile?.role === "viewing" ? "Viewing" : "Unknown"}</p>
                          <Badge variant="secondary" className="mt-1">
                            Connected
                          </Badge>
                          <Badge variant="outline" className="mt-1 ml-2">
                            Access: {spouseAccessLevel}
                          </Badge>
                        </div>
                      ) : isActorViewing && spouseProfile ? (
                        <p className="text-sm text-muted-foreground">
                          Your account is linked to a tracking account. Access level: {spouseAccessLevel}.
                        </p>
                      ) : !isActorTracking ? (
                        <p className="text-sm text-muted-foreground">
                          You are not currently linked to a tracking account.
                        </p>
                      ) : couple.goal === "Single" && invites.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          When you're ready, you can invite your future spouse to
                          share your tracking data.
                        </p>
                      ) : invites.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No pending invitations.
                        </p>
                      ) : null}
                    </div>

                    <div className="space-y-3">
                      {isActorTracking && isActorOwner && !spouseProfile &&
                        !couple.partner_user_id && (
                          <div className="space-y-3">
                            <div>
                              <Label htmlFor="inviteEmail">
                                {couple.goal === "Single"
                                  ? "Invite Your Future Spouse (Optional)"
                                  : "Invite Your Spouse"}
                              </Label>
                              <p className="text-sm text-muted-foreground mt-1 mb-2">
                                {couple.goal === "Single"
                                  ? "When you're ready to share your tracking data with your future spouse"
                                  : "Send an invitation to your spouse's email address"}
                              </p>
                              <div className="flex flex-col gap-2 sm:flex-row">
                                <Input
                                  id="inviteEmail"
                                  type="email"
                                  placeholder="spouse@example.com"
                                  value={inviteEmail}
                                  onChange={(e) => setInviteEmail(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSendInvite();
                                    }
                                  }}
                                  className="w-full"
                                />
                                <Button
                                  onClick={handleSendInvite}
                                  disabled={sendingInvite || !inviteEmail}
                                  variant={
                                    couple.goal === "Single" ? "outline" : "default"
                                  }
                                  className="w-full sm:w-auto"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send
                                </Button>
                              </div>
                            </div>
                            {duplicateInvite && (
                              <Card className="border-amber-500/40 bg-amber-500/10 p-3">
                                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                                  Pending invite already exists
                                </p>
                                <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-100/80">
                                  {duplicateInvite.invitee_email} already has an active spouse invite.
                                </p>
                                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResendInvite(duplicateInvite)}
                                    className="w-full sm:w-auto"
                                  >
                                    <Mail className="mr-2 h-4 w-4" />
                                    Resend Invite
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCopyInviteLink(duplicateInvite)}
                                    className="w-full sm:w-auto"
                                  >
                                    <Link2 className="mr-2 h-4 w-4" />
                                    Copy Existing Link
                                  </Button>
                                </div>
                              </Card>
                            )}
                          </div>
                        )}

                      {hasActiveSpouseLink && isActorTracking && isActorOwner && (
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                              Spouse Access Level
                            </Label>
                            <Select
                              value={spouseAccessLevel}
                              onValueChange={(value) =>
                                void handleUpdateSpouseAccess(value as "read" | "write")
                              }
                              disabled={updatingSpouseAccess}
                            >
                              <SelectTrigger className="mt-1 w-full">
                                <SelectValue placeholder="Access level" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="read">Read</SelectItem>
                                <SelectItem value="write">Write</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowRemoveSpouseDialog(true)}
                            className="text-destructive hover:text-destructive w-full sm:w-auto"
                          >
                            <Trash2 className="h-3 w-3 mr-2" />
                            Remove Spouse
                          </Button>
                        </div>
                      )}

                      {!hasActiveSpouseLink && (incomingInvites.length > 0 || availableInvites.length > 0) && (
                        <div className="space-y-3">
                          {[...incomingInvites, ...availableInvites].map((invite) => (
                            <Card key={invite.id} className="p-4">
                              <div className="space-y-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {invite.counterparty_email || invite.invitee_email}
                                  </p>
                                  {invite.counterparty_name && (
                                    <p className="text-xs text-muted-foreground truncate mt-1">
                                      {invite.counterparty_name}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge
                                      variant={
                                        isInviteExpired(invite.expires_at)
                                          ? "destructive"
                                          : "secondary"
                                      }
                                    >
                                      {isInviteExpired(invite.expires_at)
                                        ? "Expired"
                                        : "Pending"}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {isInviteExpired(invite.expires_at)
                                        ? "Expired "
                                        : "Expires "}
                                      {new Date(
                                        invite.expires_at
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {isInviteExpired(invite.expires_at) && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {isIncomingInvite(invite)
                                        ? "Ask them to resend this invite from their Settings."
                                        : "Resend to give this invite a new expiration date."}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                  {isIncomingInvite(invite) ? (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="default"
                                        disabled={
                                          processingInviteId === invite.id ||
                                          isInviteExpired(invite.expires_at)
                                        }
                                        onClick={() => handleAcceptInvite(invite.id)}
                                        className="w-full sm:w-auto"
                                      >
                                        Accept
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={processingInviteId === invite.id}
                                        onClick={() => handleDeclineInvite(invite.id)}
                                        className="w-full sm:w-auto"
                                      >
                                        Decline
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleResendInvite(invite)}
                                        className="w-full sm:w-auto"
                                      >
                                        <Mail className="mr-2 h-4 w-4" />
                                        Resend
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleCopyInviteLink(invite)}
                                        className="w-full sm:w-auto"
                                      >
                                        <Link2 className="mr-2 h-4 w-4" />
                                        Copy Link
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          handleCancelInvite(invite.id)
                                        }
                                        className="w-full sm:w-auto"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Cancel
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {isActorTracking && isActorOwner && couple?.id && (
            <div id={SETTINGS_ANCHORS.instructorSharing} className="scroll-mt-4">
              <InstructorSharingSection
                clientUserId={couple.id}
                senderName={
                  `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || "Your client"
                }
              />
            </div>
          )}

          <div className="space-y-4 pt-4 border-t">
            <div>
              <Label className="text-base font-semibold">Appearance</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                Choose how the app looks on this device.
              </p>
              <Select
                value={themePreference}
                onValueChange={(value) => void handleThemePreferenceChange(value as ThemePreference)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isSubscriptionManagementAvailable && !isActorInstructor && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-start justify-between gap-3">
                <Label className="text-base font-semibold">Subscription</Label>
                {hasFounderPricing && (
                  <LifetimeDiscountBadge className="text-[11px] shrink-0" />
                )}
              </div>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>Status:</span>
                  <Badge
                    variant={
                      subscriptionStatus === "active"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {subscriptionStatus === "trialing"
                      ? "Access Active"
                      : subscriptionStatus === "active"
                      ? "Active"
                      : subscriptionStatus === "past_due"
                      ? "Payment Due"
                      : "Inactive"}
                  </Badge>
                </div>
                {couple && (() => {
                  const msg = deriveSubscriptionDateMessaging({
                    subscriptionStatus: subscriptionStatus ?? null,
                    trialEndDate: trialEndDate ?? null,
                    subscriptionEndDate: subscriptionEndDate ?? null,
                    subscriptionRenewalDate: subscriptionRenewalDate ?? null,
                    subscriptionAutoRenews: subscriptionAutoRenews ?? null,
                    freeForLife,
                  });
                  if (!msg) return null;
                  return (
                    <div className="flex items-center justify-between">
                      <span>{msg.label}:</span>
                      <span className="font-medium">
                        {new Date(msg.date).toLocaleDateString()}
                      </span>
                    </div>
                  );
                })()}
                {(subscriptionStatus === "active" ||
                  subscriptionStatus === "trialing" ||
                  freeForLife) && (
                  <div className="flex items-center justify-between">
                    <span>Plan:</span>
                    <span className="font-medium">
                      {settingsCurrentPlanLabel}
                      {settingsCurrentPriceLabel
                        ? ` (${settingsCurrentPriceLabel})`
                        : ""}
                    </span>
                  </div>
                )}
                {(hasStripeSubscription || hasAppleSubscription) && (
                  <div className="flex items-center justify-between">
                    <span>Billing:</span>
                    <span className="font-medium">{billingProviderLabel}</span>
                  </div>
                )}
                {subscriptionStatus === "trialing" &&
                  hasStripeSubscription && (
                    <p className="text-xs text-primary">
                      Your selected plan is set and will start automatically after the current access period.
                    </p>
                  )}
                {subscriptionStatus === "trialing" &&
                  hasAppleSubscription && (
                    <p className="text-xs text-primary">
                      Apple will show the renewal terms for your introductory offer in the App Store.
                    </p>
                  )}
                <div className="pt-2">
                  <Button
                    onClick={billingProvider === "apple" && !isNativeIosPurchasesAvailable() ? undefined : () => navigate(ROUTES.subscription)}
                    disabled={billingProvider === "apple" && !isNativeIosPurchasesAvailable()}
                    variant="outline"
                    className="w-full"
                  >
                    {billingProvider === "apple" && !isNativeIosPurchasesAvailable()
                      ? "Managed by Apple"
                      : "Manage Subscription"}
                  </Button>
                  {billingProvider === "apple" && !isNativeIosPurchasesAvailable() && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Need to switch from Apple-managed billing?{" "}
                      <a href={SUPPORT_MAILTO_URL} className="text-primary hover:underline">
                        Contact support
                      </a>
                      .
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isActorInstructor && (
          <div className="space-y-4 pt-4 border-t">
            <div>
              <Label className="text-base font-semibold">
                Notification Settings
              </Label>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                Configure how and when you'd like to receive daily reminders.
              </p>

              <div className="space-y-6">
                {/* Daily Reminders Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="notifications" className="text-base">
                      Daily Reminders
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Get a daily reminder to log your observations. We'll ask for
                      notification permission on this device or browser.
                    </p>
                  </div>
                  <Switch
                    id="notifications"
                    checked={pushNotificationsEnabled}
                    onCheckedChange={async (checked) => {
                      // One toggle drives reminders on every platform. Scheduling is
                      // platform-dependent (Web Notifications in the browser, local
                      // notifications on iOS/Android) and handled in
                      // syncDailyReminderNotifications. We move both stored flags
                      // together so the toggle reflects the real, scheduled state.
                      setNotificationsEnabled(checked);
                      setPushNotificationsEnabled(checked);

                      const result = await handleNotificationUpdate(
                        {
                          notificationsEnabled: checked,
                          pushNotificationsEnabled: checked,
                        },
                        {
                          allowPermissionPrompt: checked,
                          showSuccessToast: true,
                        }
                      );

                      trackEvent("notification_setting_changed", {
                        setting: "daily_reminders",
                        enabled: checked,
                        platform: Capacitor.getPlatform(),
                        success: result.success,
                        permissionGranted: result.success
                          ? result.permissionGranted ?? null
                          : null,
                      });

                      if (!result.success) {
                        setNotificationsEnabled(!checked);
                        setPushNotificationsEnabled(!checked);
                      } else if (checked && result.permissionGranted === false) {
                        // Permission was denied — reflect that reminders aren't active.
                        setNotificationsEnabled(false);
                        setPushNotificationsEnabled(false);
                      }
                    }}
                  />
                </div>

                {pushNotificationsEnabled && (
                  <>
                    {/* Reminder Time */}
                    <div className="p-4 border rounded-lg">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="time" className="text-base">
                            Reminder Time
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Choose what time you'd like to receive your daily
                            reminder
                          </p>
                        </div>
                        <Input
                          id="time"
                          type="time"
                          value={notificationTime}
                          onChange={async (e) => {
                            const previousTime = notificationTime;
                            const nextTime = normalizeNotificationTime(
                              e.target.value
                            );
                            setNotificationTime(nextTime);

                            const result = await handleNotificationUpdate(
                              { notificationTime: nextTime },
                              { showSuccessToast: true }
                            );

                            trackEvent("notification_setting_changed", {
                              setting: "reminder_time",
                              value: nextTime,
                              success: result.success,
                            });

                            if (!result.success) {
                              setNotificationTime(previousTime);
                            }
                          }}
                          className="w-[10.5rem] max-w-full min-w-0 sm:shrink-0"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          )}

          {couple && instructorEmail && (
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base font-semibold">NFP Instructor</Label>
              <div className="text-sm text-muted-foreground space-y-1">
                {instructorName && (
                  <p>
                    <strong>Name:</strong> {instructorName}
                  </p>
                )}
                <p>
                  <strong>Email:</strong> {instructorEmail}
                </p>
                <p className="text-xs pt-2">
                  This instructor information is saved from when you last sent a
                  chart. You can update it by sending another chart from the
                  Dashboard.
                </p>
              </div>
            </div>
          )}

          {couple && isActorTracking && (
            <div id={SETTINGS_ANCHORS.trackedFields} className="space-y-4 pt-4 border-t scroll-mt-4">
              <div>
                <Label className="text-base font-semibold">
                  Optional Fields in Tracking UI
                </Label>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Choose which optional fields to show when logging daily
                  observations
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="field-temperature">Temperature</Label>
                    <Switch
                      id="field-temperature"
                      checked={optionalFields.temperature ?? true}
                      onCheckedChange={(checked) =>
                        setOptionalFields({
                          ...optionalFields,
                          temperature: checked,
                        })
                      }
                    />
                  </div>
                  <div className="border rounded-lg">
                    <div className="flex items-center justify-between p-3">
                      <Label htmlFor="field-mucus">Mucus</Label>
                      <Switch
                        id="field-mucus"
                        checked={optionalFields.mucus ?? true}
                        onCheckedChange={(checked) =>
                          setOptionalFields({
                            ...optionalFields,
                            mucus: checked,
                          })
                        }
                      />
                    </div>
                    {(optionalFields.mucus ?? true) && (
                      <div className="border-t bg-muted/30 px-3 py-3 space-y-3 rounded-b-lg">
                        <p className="text-xs text-muted-foreground">
                          Extra mucus details — turn these off to keep daily
                          logging simple.
                        </p>
                        {usesExpandedMucus(couple.methods ?? []) && (
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <Label htmlFor="field-mucus-color">
                                Mucus color
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                Record the color of the day's mucus (clear,
                                cloudy, etc.)
                              </p>
                            </div>
                            <Switch
                              id="field-mucus-color"
                              checked={optionalFields.mucus_color ?? true}
                              onCheckedChange={(checked) =>
                                setOptionalFields({
                                  ...optionalFields,
                                  mucus_color: checked,
                                })
                              }
                            />
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <Label htmlFor="field-mucus-frequency">
                              Mucus frequency
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              Record how often the day's mucus sign was seen
                              (1x/2x/3x/AD)
                            </p>
                          </div>
                          <Switch
                            id="field-mucus-frequency"
                            checked={optionalFields.mucus_frequency ?? false}
                            onCheckedChange={(checked) =>
                              setOptionalFields({
                                ...optionalFields,
                                mucus_frequency: checked,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="field-monitor">Monitor</Label>
                    <Switch
                      id="field-monitor"
                      checked={optionalFields.monitor ?? true}
                      onCheckedChange={(checked) =>
                        setOptionalFields({
                          ...optionalFields,
                          monitor: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 border rounded-lg">
                    <div>
                      <Label htmlFor="field-ovulation-test">Ovulation test (OPK)</Label>
                      <p className="text-xs text-muted-foreground">
                        Log a positive/negative LH test strip result for the day
                      </p>
                    </div>
                    <Switch
                      id="field-ovulation-test"
                      checked={optionalFields.ovulation_test ?? false}
                      onCheckedChange={(checked) =>
                        setOptionalFields({
                          ...optionalFields,
                          ovulation_test: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="field-bleeding">Bleeding</Label>
                    <Switch
                      id="field-bleeding"
                      checked={optionalFields.bleeding ?? true}
                      onCheckedChange={(checked) =>
                        setOptionalFields({
                          ...optionalFields,
                          bleeding: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="field-intercourse">Intercourse</Label>
                    <Switch
                      id="field-intercourse"
                      checked={optionalFields.intercourse ?? true}
                      onCheckedChange={(checked) =>
                        setOptionalFields({
                          ...optionalFields,
                          intercourse: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="field-symptoms">Symptoms</Label>
                    <Switch
                      id="field-symptoms"
                      checked={optionalFields.symptoms ?? true}
                      onCheckedChange={(checked) =>
                        setOptionalFields({
                          ...optionalFields,
                          symptoms: checked,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="field-notes">Notes</Label>
                    <Switch
                      id="field-notes"
                      checked={optionalFields.notes ?? true}
                      onCheckedChange={(checked) =>
                        setOptionalFields({ ...optionalFields, notes: checked })
                      }
                    />
                  </div>
                </div>
                <Button onClick={handleUpdateCouple} className="w-full mt-4">
                  Save Field Preferences
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4 pt-4 border-t">
            <Label className="text-base font-semibold">Export Data</Label>

            <div>
              <Label>Date Range (optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-2">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "PPP")} -{" "}
                          {format(dateRange.to, "PPP")}
                        </>
                      ) : (
                        format(dateRange.from, "PPP")
                      )
                    ) : (
                      "All time"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="range"
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) =>
                      setDateRange({ from: range?.from, to: range?.to })
                    }
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleExportCSV}
                className="flex-1"
                variant="outline"
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button
                onClick={handleExportPDF}
                className="flex-1"
                variant="outline"
              >
                <FileText className="h-4 w-4 mr-2" />
                PDF
              </Button>
            </div>

            {canImportObservations && couple && (
              <div id={SETTINGS_ANCHORS.importData} className="space-y-2 pt-2 scroll-mt-4">
                <Label className="text-base font-semibold">Restore or move your NFP Tracker data</Label>
                <p className="text-sm text-muted-foreground">
                  Import a CSV exported from NFP Tracker to restore a backup or move your chart to
                  this account.
                </p>
                <Button
                  onClick={() => setCsvImportOpen(true)}
                  className="w-full"
                  variant="outline"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import an NFP Tracker export
                </Button>
                <CsvImportDialog
                  open={csvImportOpen}
                  onOpenChange={setCsvImportOpen}
                  ownerUserId={couple.id}
                  actorUserId={actorUserId}
                />
              </div>
            )}
          </div>

          <div className="space-y-4 pt-4 border-t">
            <Label className="text-base font-semibold">App Information</Label>
            <p className="text-sm text-muted-foreground">
              Track cycle observations, reminders, and shared records in one
              place.
            </p>
            <p className="text-sm text-muted-foreground">
              App version: {appVersion}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(ROUTES.help)}
                className="justify-start"
              >
                Help & FAQ
              </Button>
              {isSubscriptionManagementAvailable && (
                <Button
                  variant="outline"
                  onClick={() => navigate(ROUTES.subscription)}
                  className="justify-start"
                >
                  Subscription Plans
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => window.open(SUPPORT_MAILTO_URL)}
                className="justify-start"
              >
                Contact Support
              </Button>
              {termsUrl && (
                <Button
                  variant="outline"
                  onClick={() => window.open(termsUrl, "_blank", "noopener,noreferrer")}
                  className="justify-start"
                >
                  Terms of Service
                </Button>
              )}
              {privacyUrl && (
                <Button
                  variant="outline"
                  onClick={() =>
                    window.open(privacyUrl, "_blank", "noopener,noreferrer")
                  }
                  className="justify-start"
                >
                  Privacy Policy
                </Button>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} NFP Tracker. All rights
              reserved.
            </p>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <Label className="text-base font-semibold flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Danger Zone
            </Label>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              className="w-full"
            >
              Delete Account
            </Button>
          </div>

          <div className="text-xs text-center text-muted-foreground pt-4 border-t">
            <p>Educational guidance only. Not medical advice.</p>
            <p>This app tracks user-entered observations only. Consult your instructor or clinician to interpret them.</p>
          </div>
        </Card>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              account and all associated data including observations, cycles,
              and invitations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showRemoveSpouseDialog}
        onOpenChange={setShowRemoveSpouseDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Spouse?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect your spouse from this shared account. They
              will no longer have access to the data. You can send a new
              invitation later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveSpouse}>
              Remove Spouse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Settings;
